import { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RtkDropzone } from 'src/components/RtkDropzone'
import { RootState } from 'src/redux/root'
import { MaxVizFileUploads } from 'src/redux/slices/analysis/consts'
import { uploadVizFile } from 'src/redux/slices/analysis/endpoints'
import { clearReports } from 'src/redux/slices/analysis/reducers'
import {
  getFilenameSelectorByIndex,
  highestVizFileSuccess,
  isLoading,
  selectHasNoWarnings,
  uploadedFileCount,
  vizFilenames,
  vizFilesCanProcess,
  vizScenarioNames
} from 'src/redux/slices/analysis/selectors'
import { AsyncTask } from 'src/redux/slices/async-processing/consts'
import { startAsyncTask } from 'src/redux/slices/async-processing/reducers'
import {
  makeSelectTaskStatus,
  makeSelectTaskStatusMeta
} from 'src/redux/slices/async-processing/selectors'
import { useTabNavigation } from 'src/redux/slices/navigation/hooks'

import Button from '@material-ui/core/Button'
import Container from '@material-ui/core/Container'
import Grid from '@material-ui/core/Grid'

import ScenarioNameInput from '../components/ScenarioNameInput'
import WarningReports from '../components/WarningReports'

export default () => {
  const dispatch = useDispatch()
  const loading = useSelector(isLoading)
  const canProcess = useSelector(vizFilesCanProcess)
  const filenames = useSelector(vizFilenames)
  const scenarioNames = useSelector(vizScenarioNames)
  const dropzoneCount = useSelector((state: RootState) =>
    Math.min(
      Math.max(highestVizFileSuccess(state) + 1, uploadedFileCount(state) + 1),
      MaxVizFileUploads,
    ),
  )
  const hasNoWarnings = useSelector(selectHasNoWarnings)
  const reports = useSelector((state: RootState) => state.analysis.reports)
  const { goToTab } = useTabNavigation()

  const { taskLocation: url } =
    useSelector(makeSelectTaskStatusMeta(AsyncTask.processVizFiles))?.data ?? {}
  const result = useSelector(
    makeSelectTaskStatus({ endpoint: AsyncTask.processVizFiles, url: url ?? '' }),
  )
  // asyncApi.endpoints.checkAsyncStatus.select({ endpoint: AsyncTask.processVizFiles, url }),
  const { isSuccess: processingIsSuccess } = result

  // TODO: Switch to new useNavigationEffect hook
  useEffect(() => {
    if (processingIsSuccess && hasNoWarnings) {
      goToTab('settings')
      dispatch(clearReports())
    }
  }, [processingIsSuccess, hasNoWarnings, goToTab])

  const processVizFiles = useCallback(() => {
    const body = filenames
      .map((filename, index) => ({
        filename,
        scenarioName: scenarioNames[index],
        index,
      }))
      .filter(({ filename }) => filename != '')

    dispatch(startAsyncTask({ endpoint: AsyncTask.processVizFiles, body }))
  }, [filenames, scenarioNames, dispatch])

  return (
    <Container>
      {!hasNoWarnings && <WarningReports />}
      {!reports && (
        <Grid container spacing={3} justifyContent='center'>
          {Array(dropzoneCount)
            .fill(0)
            .map((_, i) => (
              <Grid container item spacing={3} justifyContent='center' key={`viz-upload-form-${i}`}>
                <Grid item xs={12} md={2}>
                  <ScenarioNameInput index={i} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <RtkDropzone
                    index={i}
                    mutation={uploadVizFile.useMutation}
                    filenameSelector={getFilenameSelectorByIndex}
                    label='UMI file for analysis'
                  />
                </Grid>
              </Grid>
            ))}

          <Grid item>
            <Button
              onClick={processVizFiles}
              variant='outlined'
              size='large'
              disabled={!canProcess || loading}
            >
              Process files
            </Button>
          </Grid>
        </Grid>
      )}
    </Container>
  )
}
